import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "./style.module.scss";
import { Spin, notification, Button, Select, Modal, Progress } from "antd";
import AllDashboardsHeader from "components/AllDashboards/AllDashboardsHeader";
import { apiRequest } from "util/services";
import { UNLOGGED } from "util/constants";
import { useHistory } from "react-router-dom";
import DashboardItem from "components/AllDashboards/DashboardItem";
import * as actions from "redux/popup/actions.js";
import { useDispatch, useSelector } from "react-redux";
import { DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { fetchDashboardReviewTrendsBatch } from "util/dashboardReviewTrends";
import { useEnterprisePowerUser } from "hooks/useEnterprisePowerUser";
const { Option } = Select;

/** One delete at a time; gap reduces API/DB load on large bulk selections. */
const DELETE_QUEUE_GAP_MS = 900;
/** Extra attempts after the first failure (total tries = 1 + this). */
const DELETE_MAX_RETRIES = 2;
const DELETE_RETRY_GAP_MS = 1200;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isDeleteSuccess = (result) =>
  result &&
  typeof result === "object" &&
  !result.cancel &&
  !result.error &&
  Boolean(result.success);

const NewDashboard = ({ authenticated, user }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [localState, setState] = useState({
    activePlatform: 0,
    selectedUserId: user?.uid,
    group_id: null,
  });
  const [loading, setLoading] = useState(true);
  const [platforms, setPlatforms] = useState([]);
  const [startIndex, setStartIndex] = useState(0);
  const [lastIndex, setLastIndex] = useState();
  const [Dashboards, setDashboards] = useState([]);
  const [groups, setGroups] = useState([]);

  const [isOnlyPage, setIsOnlyPage] = useState(true);
  const [searchWord, setSearchWord] = useState("");
  const [sortValue, setSortValue] = useState("newest");
  const [selectedPerPage, setSelectedPerPage] = useState(10);
  const [dashboardPageNumber, setDashboardPageNumber] = useState(1);
  const [totalDashboards, setTotalDashboards] = useState();
  const [numOfDashboards, setNumOfDashboards] = useState(10);
  const [pinnedCount, setPinnedCount] = useState(0);
  const [viewMode, setViewMode] = useState("grid");
  const [dashboardTrends, setDashboardTrends] = useState({});
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [selectedDashboardIds, setSelectedDashboardIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deleteQueue, setDeleteQueue] = useState({
    active: false,
    total: 0,
    done: 0,
    success: 0,
    failed: 0,
  });
  const [failedDeleteIds, setFailedDeleteIds] = useState([]);
  const deleteQueueLock = useRef(false);
  const { t } = useTranslation();
  const userReducer = useSelector((state) => state.userReducer);
  const theme = useSelector((state) => state.DashboardReducer.theme);
  const isEnterprisePowerUser = useEnterprisePowerUser();
  const deleteQueueActive = deleteQueue.active;

  //this in WP
  useEffect(() => {
    if (authenticated === UNLOGGED) {
      history.push("/");
      return;
    }
    getAllPlatforms();
    dispatch({
      type: actions.POPUP_FIRST_TIME,
    });
  }, []);

  useEffect(() => {
    fetchAllGroups();
  }, []);

  useEffect(() => {
    getAllDashboards();
  }, [
    startIndex,
    localState.activePlatform,
    localState.selectedUserId,
    localState.group_id,
    sortValue,
    numOfDashboards,
  ]);

  const dashboardIdsKey = useMemo(
    () =>
      (Dashboards || [])
        .map((row) => String(row?.ID))
        .filter(Boolean)
        .join(","),
    [Dashboards],
  );

  useEffect(() => {
    let cancelled = false;
    const ids = (Dashboards || [])
      .map((row) => parseInt(row?.ID, 10))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      setDashboardTrends({});
      setTrendsLoading(false);
      return undefined;
    }
    setTrendsLoading(true);
    fetchDashboardReviewTrendsBatch(ids, localState?.selectedUserId)
      .then((trends) => {
        if (cancelled) return;
        setDashboardTrends(trends || {});
      })
      .catch(() => {
        if (!cancelled) setDashboardTrends({});
      })
      .finally(() => {
        if (!cancelled) setTrendsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dashboardIdsKey, localState?.selectedUserId]);

  const getAllPlatforms = async () => {
    const result = await apiRequest(
      "users/getAllowedPlatformsOfUser",
      "POST",
      {
        firebase_id: user.uid,
      },
      true
    );
    if (!result.error) {
      setPlatforms(result.platforms);
    }
  };
  const fetchAllGroups = async () => {
    const result = await apiRequest("dashboards/groups", "POST", {}, true);
    if (!result.error) {
      setGroups(result.success);
    } else {
      notification.error({
        message: result.error,
      });
    }
  };

  const sortByPinnedDashboards = (dashboards) => {
    let pinned = [];
    let unpinned = [];
    if (dashboards?.length) {
      dashboards.map((info) => {
        if (info?.pined) {
          pinned.push(info);
        } else {
          unpinned.push({ ...info });
        }
      });
    }
    setPinnedCount(pinned.length);
    return [...pinned, ...unpinned];
  };

  const getAllDashboards = async (reset) => {
    // Avoid list refresh clobbering the in-progress delete queue.
    if (deleteQueueLock.current) return;
    // if (startIndex === lastIndex) return
    setLoading(true);
    setSelectedDashboardIds([]);
    const payload = {
      start_index: startIndex,
      num_dashboards: numOfDashboards,
      sort: sortValue,
    };

    if (searchWord && !reset) {
      payload.search = searchWord;
    }
    if (localState?.selectedUserId) {
      payload.user_id = localState?.selectedUserId;
    }

    if (localState.activePlatform !== 0)
      payload.platform_id = localState.activePlatform;
    if (localState.group_id) payload.group_id = localState.group_id;

    const result = await apiRequest("dashboards/all", "POST", payload, true);
    if (result && result?.dashboards?.length !== 0) {
      setDashboards(sortByPinnedDashboards(result?.dashboards));
      setTotalDashboards(result?.num_dashboard);
      if (startIndex === 0 && result?.dashboards?.length === numOfDashboards) {
        setIsOnlyPage(false);
      }
      if (startIndex === 0 && result?.dashboards?.length < numOfDashboards) {
        setIsOnlyPage(true);
      }
    } else if (result && result?.dashboards?.length === 0 && startIndex !== 0) {
      notification.info({
        message: t("notifications.1"),
        icon: (
          <InfoCircleOutlined
            style={{ color: theme === "dark" ? "white" : "black" }}
          />
        ),
      });
      setLastIndex(startIndex - 1);
      setStartIndex(startIndex - 1);
    } else if (result.error) {
      notification.error({
        message: result.error,
      });
    } else if (result?.dashboards?.length === 0) {
      setDashboards([]);
      setTotalDashboards(0);
    }
    setLoading(false);
  };

  const handleUserPage = (e) => {
    if (e.which === 13) {
      if (!dashboardPageNumber) {
        notification.info({
          message: t("notifications.2"),
          icon: (
            <InfoCircleOutlined
              style={{ color: theme === "dark" ? "white" : "black" }}
            />
          ),
        });
      } else if (
        dashboardPageNumber > 0 &&
        dashboardPageNumber <= Math.ceil(totalDashboards / selectedPerPage) &&
        /^\d+$/.test(dashboardPageNumber)
      ) {
        setStartIndex(dashboardPageNumber - 1);
        e.target.blur();
      } else {
        notification.info({
          message: t("notifications.2"),
          icon: (
            <InfoCircleOutlined
              style={{ color: theme === "dark" ? "white" : "black" }}
            />
          ),
        });
      }
    }
  };

  const handlePageChange = (value) => {
    setSelectedPerPage(value);
    setNumOfDashboards(value);
    setStartIndex(0);
    setDashboardPageNumber(1);
  };

  const pageDashboardIds = useMemo(
    () =>
      (Dashboards || [])
        .map((row) => row?.ID)
        .filter((id) => id != null),
    [Dashboards],
  );

  const selectedOnPageCount = useMemo(
    () =>
      pageDashboardIds.filter((id) => selectedDashboardIds.includes(id)).length,
    [pageDashboardIds, selectedDashboardIds],
  );

  const allPageSelected =
    pageDashboardIds.length > 0 &&
    selectedOnPageCount === pageDashboardIds.length;
  const somePageSelected = selectedOnPageCount > 0 && !allPageSelected;

  const toggleDashboardSelection = useCallback((dashboardId, checked) => {
    setSelectedDashboardIds((prev) => {
      if (checked) {
        return prev.includes(dashboardId) ? prev : [...prev, dashboardId];
      }
      return prev.filter((id) => id !== dashboardId);
    });
  }, []);

  const handleSelectAllPage = useCallback(
    (checked) => {
      if (checked) {
        setSelectedDashboardIds(pageDashboardIds);
      } else {
        setSelectedDashboardIds([]);
      }
    },
    [pageDashboardIds],
  );

  const handleClearSelection = useCallback(() => {
    setSelectedDashboardIds([]);
  }, []);

  const runDeleteQueue = useCallback(
    async (rawIds) => {
      if (deleteQueueLock.current) return;
      const ids = (rawIds || [])
        .map((id) => Number(id))
        .filter((id) => id > 0);
      if (!ids.length) return;

      deleteQueueLock.current = true;
      setBulkDeleteOpen(false);
      setSelectedDashboardIds([]);
      setFailedDeleteIds([]);
      setDeleteQueue({
        active: true,
        total: ids.length,
        done: 0,
        success: 0,
        failed: 0,
      });

      let successCount = 0;
      let failCount = 0;
      const errors = [];
      const stillFailed = [];

      try {
        for (let i = 0; i < ids.length; i += 1) {
          const dashboardId = ids[i];
          let ok = false;
          let lastError = null;

          for (
            let attempt = 0;
            attempt <= DELETE_MAX_RETRIES;
            attempt += 1
          ) {
            const result = await apiRequest(
              "dashboards/v2/delete",
              "POST",
              { dashboard_id: dashboardId },
              true,
            );
            if (isDeleteSuccess(result)) {
              ok = true;
              break;
            }
            lastError = result?.error ? String(result.error) : null;
            if (attempt < DELETE_MAX_RETRIES) {
              await sleep(DELETE_RETRY_GAP_MS);
            }
          }

          if (ok) {
            successCount += 1;
            setDashboards((prev) =>
              (prev || []).filter((row) => Number(row?.ID) !== dashboardId)
            );
            setTotalDashboards((prev) =>
              typeof prev === "number" && prev > 0 ? prev - 1 : prev
            );
          } else {
            failCount += 1;
            stillFailed.push(dashboardId);
            if (lastError) errors.push(lastError);
          }

          setDeleteQueue({
            active: true,
            total: ids.length,
            done: i + 1,
            success: successCount,
            failed: failCount,
          });

          if (i < ids.length - 1) {
            await sleep(DELETE_QUEUE_GAP_MS);
          }
        }
      } finally {
        deleteQueueLock.current = false;
        setDeleteQueue((prev) => ({ ...prev, active: false }));
      }

      await getAllDashboards();

      if (stillFailed.length) {
        setFailedDeleteIds(stillFailed);
        setSelectedDashboardIds(stillFailed);
      }

      if (successCount > 0) {
        notification.success({
          message: t("all_dashboards_page.50", { count: successCount }),
        });
      }
      if (failCount > 0) {
        notification.error({
          message:
            errors[0] || t("all_dashboards_page.51", { count: failCount }),
        });
      }
    },
    [t, getAllDashboards],
  );

  const handleBulkDelete = useCallback(async () => {
    await runDeleteQueue(selectedDashboardIds);
  }, [selectedDashboardIds, runDeleteQueue]);

  const handleRetryFailedDeletes = useCallback(async () => {
    if (!failedDeleteIds.length) return;
    await runDeleteQueue(failedDeleteIds);
  }, [failedDeleteIds, runDeleteQueue]);

  const handleDismissFailedDeletes = useCallback(() => {
    setFailedDeleteIds([]);
  }, []);

  const DashboardsItems = Dashboards?.map((Dashboard) => {
    return (
      <DashboardItem
        key={Dashboard.ID}
        item={Dashboard}
        setDashboards={setDashboards}
        pinnedCount={pinnedCount}
        Dashboards={Dashboards}
        groups={groups}
        gridView={viewMode === "grid"}
        getAllDashboards={getAllDashboards}
        IsExpert={userReducer.IsExpert}
        trend={dashboardTrends[String(Dashboard.ID)] ?? null}
        trendsLoading={trendsLoading}
        selectionEnabled={isEnterprisePowerUser && !deleteQueueActive}
        selected={selectedDashboardIds.includes(Dashboard.ID)}
        onSelectionChange={toggleDashboardSelection}
      />
    );
  });

  return (
    <div className={styles.mainWindow}>
      <div>
        <AllDashboardsHeader
          platforms={platforms}
          localState={localState}
          setState={setState}
          setStartIndex={setStartIndex}
          setLastIndex={setLastIndex}
          groups={groups}
          user={user}
          getAllDashboards={getAllDashboards}
          startIndex={startIndex}
          setSearchWord={setSearchWord}
          searchWord={searchWord}
          setSortValue={setSortValue}
          sortValue={sortValue}
          setDashboardPageNumber={setDashboardPageNumber}
          viewMode={viewMode}
          setViewMode={setViewMode}
          selectionEnabled={
            isEnterprisePowerUser && pageDashboardIds.length > 0
          }
          selectAllChecked={allPageSelected}
          selectAllIndeterminate={somePageSelected}
          onSelectAll={handleSelectAllPage}
          selectedCount={selectedDashboardIds.length}
          selectionDisabled={deleteQueueActive}
        />
        {isEnterprisePowerUser && selectedDashboardIds.length > 0 ? (
          <div
            className={styles.bulkActionBar}
            role="region"
            aria-label={t("all_dashboards_page.59", {
              count: selectedDashboardIds.length,
            })}
          >
            <div className={styles.bulkActionBarLeft}>
              <span className={styles.bulkActionCount}>
                {t("all_dashboards_page.59", {
                  count: selectedDashboardIds.length,
                })}
              </span>
              <button
                type="button"
                className={styles.bulkActionClear}
                onClick={handleClearSelection}
                disabled={deleteQueueActive}
              >
                {t("all_dashboards_page.60")}
              </button>
            </div>
            <div className={styles.bulkActionBarRight}>
              <Button
                type="primary"
                icon={<DeleteOutlined />}
                onClick={() => setBulkDeleteOpen(true)}
                disabled={deleteQueueActive}
              >
                {t("all_dashboards_page.12")}
              </Button>
            </div>
          </div>
        ) : null}
        {deleteQueueActive ? (
          <div className={styles.deleteQueueBanner}>
            <div className={styles.deleteQueueText}>
              {t("all_dashboards_page.54", {
                done: deleteQueue.done,
                total: deleteQueue.total,
              })}
            </div>
            <Progress
              percent={
                deleteQueue.total
                  ? Math.round((deleteQueue.done / deleteQueue.total) * 100)
                  : 0
              }
              status="active"
              showInfo
            />
          </div>
        ) : null}
        {!deleteQueueActive && failedDeleteIds.length > 0 ? (
          <div className={styles.deleteQueueBanner}>
            <div className={styles.deleteQueueFailedRow}>
              <div className={styles.deleteQueueText}>
                {t("all_dashboards_page.56", {
                  count: failedDeleteIds.length,
                })}
              </div>
              <div className={styles.deleteQueueFailedActions}>
                <Button
                  type="primary"
                  onClick={handleRetryFailedDeletes}
                  disabled={deleteQueueActive}
                >
                  {t("all_dashboards_page.57", {
                    count: failedDeleteIds.length,
                  })}
                </Button>
                <Button onClick={handleDismissFailedDeletes}>
                  {t("all_dashboards_page.58")}
                </Button>
              </div>
            </div>
          </div>
        ) : null}
        <Spin spinning={loading || deleteQueueActive}>
          <div className={styles.content} id="all_Dashboards_container">
            {DashboardsItems.length !== 0 && viewMode === "list" && (
              <DashboardItem
                item={{}}
                header
                selectionEnabled={isEnterprisePowerUser}
              />
            )}
            {DashboardsItems.length === 0 && !loading ? (
              <div className={styles.noBoards}>
                <p className={styles.heading}>
                  {t("empty_states.5")}{" "}
                  <img
                    src={
                      theme === "dark"
                        ? "/assets/emptyStates/all_dashboards_icon.svg"
                        : "/assets/emptyStates/all_dashboards_icon_black.svg"
                    }
                    alt=""
                  />
                </p>
                <div className={styles.secondRow}>
                  <img
                    src={
                      theme === "dark"
                        ? "/assets/error_outline.svg"
                        : "/assets/error_outline_black.svg"
                    }
                    alt=""
                  />
                  {t("empty_states.6")}
                </div>
                <p className={styles.secondHeading}>
                  {t("empty_states.7")}{" "}
                  <img src={"/assets/double_down_arrow.svg"} alt="" />
                </p>
                <div className={styles.imgBlock}>
                  <div>
                    <img
                      src={
                        theme === "dark"
                          ? "/assets/emptyStates/all_dashboards1.png"
                          : theme === "light"
                          ? "/assets/emptyStates/all_dashboards1_light.png"
                          : "/assets/emptyStates/all_dashboards1_brand.png"
                      }
                      alt=""
                    />
                  </div>
                  <div>
                    <img
                      src={
                        theme === "dark"
                          ? "/assets/emptyStates/all_dashboards2.png"
                          : theme === "light"
                          ? "/assets/emptyStates/all_dashboards2_light.png"
                          : "/assets/emptyStates/all_dashboards2_brand.png"
                      }
                      alt=""
                    />
                  </div>
                </div>
              </div>
            ) : viewMode === "grid" ? (
              <div className={styles.grid}>{DashboardsItems}</div>
            ) : (
              DashboardsItems
            )}
          </div>
        </Spin>
      </div>
      {Dashboards.length !== 0 && (
        <div className={styles.paginationWrapper}>
          <div className={styles.innerWrapper}>
            {
              <div className={styles.paginationBox}>
                <Button
                  disabled={startIndex === 0}
                  onClick={() => {
                    parseInt(dashboardPageNumber, 10) === startIndex + 1 &&
                      setStartIndex((prev) => prev - 1);
                    parseInt(dashboardPageNumber, 10) === startIndex + 1 &&
                      setDashboardPageNumber((prev) => parseInt(prev, 10) - 1);
                  }}
                >
                  <i className="fa fa-angle-left" />
                </Button>
                <span className={styles.pagination}>
                  <input
                    className={styles.pageIndex}
                    onKeyPress={handleUserPage}
                    type="text"
                    value={dashboardPageNumber}
                    onChange={(e) => setDashboardPageNumber(e.target.value)}
                  />
                  {totalDashboards
                    ? ` / ${Math.ceil(totalDashboards / selectedPerPage)}`
                    : ""}
                </span>
                <Button
                  disabled={
                    Dashboards.length === 0 ||
                    (totalDashboards &&
                      startIndex + 1 ===
                        Math.ceil(totalDashboards / selectedPerPage)) ||
                    !dashboardPageNumber
                  }
                  onClick={() => {
                    parseInt(dashboardPageNumber, 10) === startIndex + 1 &&
                      setStartIndex((prev) => prev + 1);
                    parseInt(dashboardPageNumber, 10) === startIndex + 1 &&
                      setDashboardPageNumber((prev) => parseInt(prev, 10) + 1);
                  }}
                >
                  <i className="fa fa-angle-right" />
                </Button>
              </div>
            }

            {Dashboards?.length > 0 && (
              <Select
                className="review-page-number-selector"
                value={selectedPerPage + ` / ${t("label.10")}`}
                onChange={handlePageChange}
                style={{ backgroundColor: "transparent" }}
              >
                <Option value={10}>10</Option>
                <Option
                  disabled={totalDashboards <= 10 ? true : false}
                  value={25}
                >
                  25
                </Option>
                <Option
                  disabled={totalDashboards <= 25 ? true : false}
                  value={50}
                >
                  50
                </Option>
                <Option
                  disabled={totalDashboards <= 50 ? true : false}
                  value={100}
                >
                  100
                </Option>
              </Select>
            )}
          </div>
          {totalDashboards > 0 && (
            <div className={styles.dashboardsTotal}>
              {selectedPerPage * startIndex +
                Dashboards.filter((item) => {
                  if (!item.pined) {
                    return item;
                  }
                })?.length +
                " of " +
                totalDashboards}
            </div>
          )}
        </div>
      )}
      <Modal
        open={bulkDeleteOpen}
        title={t("all_dashboards_page.52")}
        onCancel={() => !deleteQueueActive && setBulkDeleteOpen(false)}
        onOk={handleBulkDelete}
        okText={t("all_dashboards_page.12")}
        cancelText={t("button.3")}
        confirmLoading={deleteQueueActive}
        okButtonProps={{ type: "primary", disabled: deleteQueueActive }}
        destroyOnClose
      >
        <p>{t("all_dashboards_page.53", { count: selectedDashboardIds.length })}</p>
        <p className={styles.deleteQueueHint}>{t("all_dashboards_page.55")}</p>
      </Modal>
    </div>
  );
};

export default NewDashboard;
